import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Link, useLocation } from 'react-router-dom';
import Data from '../../pages/data.json';
import { getCategoryIcon, getTagIcon } from '../../utils/constants';

const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className="icon">
    <path d={d} />
  </svg>
);

const ICONS = {
  home: 'M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z',
  grid: 'M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
  star: 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z',
  blog: 'M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z M8 9a1 1 0 000 2h4a1 1 0 100-2H8z M8 13a1 1 0 100 2h4a1 1 0 100-2H8z',
  news: 'M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z M15 7h2a2 2 0 012 2v5.5a1.5 1.5 0 01-3 0V7z',
  quiz: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z',
  moon: 'M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z',
  sun:  'M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z',
};

const SidebarLink = ({ to, icon, label, external, onClick }) => {
  const location = useLocation();
  const isActive = !external && location.pathname === to;
  const cls = `sidebar-link${isActive ? ' active' : ''}`;
  if (external) return <a href={to} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick}><Icon d={icon} /><span>{label}</span></a>;
  return <Link to={to} className={cls} onClick={onClick}><Icon d={icon} /><span>{label}</span></Link>;
};

const Sidebar = () => {
  const { darkMode, toggleDarkMode } = useAppContext();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = () => setOpen(false);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && open) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const categories = [...new Set(Data?.games?.map(g => g.categories.en).flat())];
  const tags = [...new Set(Data?.games?.map(g => g.tags?.en || []).flat())];

  return (
    <>
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center px-4 gap-3 h-[52px] bg-secondary-theme border-b-theme">
        <button onClick={() => setOpen(!open)}
          className="sm:hidden p-2 rounded-lg touch-manipulation bg-hover-theme text-sub"
          aria-label="Menu">
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" clipRule="evenodd" />
          </svg>
        </button>

        <Link to="/" className="flex items-center gap-2 no-underline flex-shrink-0">
          <img src="/logo.png" alt="TheGameZone" className="w-7 h-7 rounded-lg" />
          <span className="font-extrabold text-base hidden xs:block font-sora gradient-text">TheGameZone</span>
        </Link>

        <div className="flex-1" />

        <a href="https://9842.read.newszop.com/" target="_blank" rel="noopener noreferrer"
          className="touch-manipulation flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-bold no-underline bg-hover-theme text-sub">
          <img src="/icons/news1.png" alt="news" className="w-4 h-4" />
          <span className="hidden sm:inline">News</span>
        </a>
        <a href="https://10225.play.quizzop.com/" target="_blank" rel="noopener noreferrer"
          className="touch-manipulation flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-bold no-underline bg-hover-theme text-sub">
          <img src="/icons/quiz2.gif" alt="quiz" className="w-5 h-5" />
          <span className="hidden sm:inline">Quiz</span>
        </a>

        <button onClick={toggleDarkMode}
          className="p-1.5 rounded-lg flex items-center bg-hover-theme text-sub border-none cursor-pointer"
          aria-label="Toggle theme">
          <Icon d={darkMode ? ICONS.sun : ICONS.moon} size={16} />
        </button>
      </nav>

      {open && <div className="fixed inset-0 z-[38] bg-black/60" onClick={close} />}

      {/* SIDEBAR */}
      <aside ref={ref}
        className={`fixed top-[52px] left-0 bottom-0 z-40 w-[220px] overflow-x-hidden flex flex-col transition-transform duration-300 ease-in-out bg-secondary-theme border-r-theme ${!open ? '-translate-x-full sm:translate-x-0' : 'translate-x-0'}`}>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 pt-2.5 pb-2">
          <div className="mb-1">
            <SidebarLink to="/" icon={ICONS.home} label="Home" onClick={close} />
            <SidebarLink to="/category/all-games" icon={ICONS.grid} label="All Games" onClick={close} />
            <SidebarLink to="/blog" icon={ICONS.blog} label="Blog" onClick={close} />
            <SidebarLink to="https://11190.play.gamezop.com/" icon={ICONS.star} label="GameZop" external />
            <SidebarLink to="http://freshheadlines.in/" icon={ICONS.news} label="News" external />
            <SidebarLink to="https://10225.play.quizzop.com/" icon={ICONS.quiz} label="Quiz" external />
          </div>

          <div className="mt-3">
            <div className="px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-dim">Categories</div>
            {categories.map((cat, i) => (
              <Link key={i} to={`/category/${cat.toLowerCase().replace(/ /g, '-')}`} onClick={close}
                className={`sidebar-link${location.pathname === `/category/${cat.toLowerCase().replace(/ /g, '-')}` ? ' active' : ''}`}>
                <span className="text-base w-5 text-center flex-shrink-0">{getCategoryIcon(cat)}</span>
                <span>{cat}</span>
              </Link>
            ))}
          </div>

          <div className="mt-3">
            <div className="px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-dim">Tags</div>
            {tags.map((tag, i) => (
              <Link key={i} to={`/tag/${tag.toLowerCase().replace(/ /g, '-')}`} onClick={close}
                className={`sidebar-link${location.pathname === `/tag/${tag.toLowerCase().replace(/ /g, '-')}` ? ' active' : ''}`}>
                <span className="text-base w-5 text-center flex-shrink-0">{getTagIcon(tag)}</span>
                <span>{tag}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="flex-shrink-0 p-3 border-t-theme bg-secondary-theme">
          <button onClick={toggleDarkMode}
            className="w-full px-3 py-2.5 rounded-xl flex items-center gap-2 text-sm font-bold cursor-pointer bg-hover-theme border-theme text-sub font-nunito">
            <Icon d={darkMode ? ICONS.sun : ICONS.moon} size={16} />
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
